import React, {useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux"

import {Box} from "@mui/material";

import Header from "./components/Header";
import PerformanceResume from "./components/PerformanceResume";
import SaleValuesAndVolume from "./components/SaleValuesAndVolume";
import SecondaryKpis from "./components/SecondaryKpis";
import TopProducts from "./components/TopProducts";
import TopRetailers from "./components/TopRetailers";
import StockAlerts from "./components/StockAlerts";

import {DateRangePickerContext} from "contexts/DateRangePickerContext";

import {getOrdersData} from "api/dashboard/ordersData";
import showRetailer from "api/retailer/show";
import {UserAgentUtils} from "../../../../utils/useragent";

function Dashboard({ retailer, member, currentOrganization }) {
    const {
        startDate,
        endDate,
        unit,
        unitParser,
        unitTooltip,
        dateChangedTrigger,
    } = useContext(DateRangePickerContext);

    const [ordersData, setOrdersData] = useState(null);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [retailerName, setRetailerName] = useState(null);

    const isMobile = UserAgentUtils.isMobile();

    useEffect(() => {
        if (!retailer) {
            setRetailerName(null);
            return;
        }

        showRetailer(retailer)
            .then(data => setRetailerName(data?.name ?? null))
            .catch(() => setRetailerName(null));
    }, [retailer]);

    useEffect(() => {
        if (!startDate || !endDate) {
            return;
        }

        const start = startDate.format("Y-M-D");
        const end = endDate.format("Y-M-D");

        setOrdersLoading(true);

        getOrdersData(start, end, unit, retailer)
            .then(data => setOrdersData(data))
            .catch(() => setOrdersData(null))
            .finally(() => setOrdersLoading(false));
    }, [dateChangedTrigger, retailer]);

    const currency = currentOrganization?.currency;
    const organizationName = currentOrganization?.name ?? "";

    const displayName = retailer ? (retailerName ?? "") : organizationName;

    return (
        <Box sx={{
            backgroundColor: "#f8f9fb",
            minHeight: "100vh",
            marginLeft: {
                md: "240px"
            }
        }}>
            <Header member={member} />

            <Box sx={{ p: { xs: "16px", sm: "24px 32px", md: "32px 40px" } }}>

                <PerformanceResume
                    ordersData={ordersData}
                    loading={ordersLoading}
                    currency={currency}
                    organizationName={displayName}
                    retailer={retailer}
                />

                <div style={{ marginTop: isMobile ? '32px' : '48px' }}>
                    <SaleValuesAndVolume
                        ordersData={ordersData}
                        loading={ordersLoading}
                        unit={unit}
                        unitParser={unitParser}
                        unitTooltip={unitTooltip}
                        currency={currency}
                    />
                </div>

                <div style={{ marginTop: isMobile ? '32px' : '48px' }}>
                    <SecondaryKpis
                        ordersData={ordersData}
                        loading={ordersLoading}
                        currency={currency}
                        hasSyliusStockAlertFeature={currentOrganization?.syliusStockAlertFeature ?? false}
                    />
                </div>

                <div style={{ marginTop: isMobile ? '32px' : '48px' }}>
                    <TopProducts retailer={retailer} entityName={displayName} organizationName={organizationName} />
                </div>

                {!retailer && (
                    <div style={{ marginTop: isMobile ? '32px' : '48px' }}>
                        <TopRetailers currency={currency} organizationName={organizationName} />
                    </div>
                )}

                <div style={{ marginTop: isMobile ? '32px' : '48px' }}>
                    <StockAlerts retailer={retailer} currency={currency} entityName={displayName} />
                </div>
            </Box>
        </Box>
    );
}

Dashboard.propTypes = {
    retailer: PropTypes.string,
    member: PropTypes.object,
    currentOrganization: PropTypes.object,
};

const mapStateToProps = (state) => ({
    member: state.authentication.member,
    currentOrganization: state.currentOrganization.retrieved ?? null,
});

export default connect(mapStateToProps)(Dashboard);
